<template>
  <div class="ui ui-tutorial" @click="firstClick">
    <span class="ui-tutorial__bg" />

    <transition @enter="enter" @leave="leave" appear mode="out-in">
      <section class="ui-tutorial__phase" data-phase="tap" v-if="phase === 0" key="tap">
        <p class="ui-tutorial__content" v-html="$translate('tutorial').content[0]"/>

        <button class="ui-tutorial__circle">
          <span class="circle__repeater" v-for="n in 6" :key="n"/>

          <p>{{ $translate('tutorial').button }}</p>
        </button>
      </section>

      <section class="ui-tutorial__phase" data-phase="leaf" v-if="phase === 1" key="leaf">
        <p class="ui-tutorial__content" v-html="$translate('tutorial').content[1]"/>

        <div class="ui-tutorial__leaf">
          <img class="leaf__image" src="/img/misc/leaf-big.png" alt="leaf icon image">
          <p>x10</p>
        </div>
      </section>

      <section class="ui-tutorial__phase" data-phase="seeds" v-if="phase === 2" key="seeds">
        <p class="ui-tutorial__content" v-html="$translate('tutorial').content[2]"/>

        <div class="ui-tutorial__seeds">
          <img src="/img/misc/nut.png" alt="Nut">
          <p>or</p>
          <img src="/img/misc/sunflower-seed.png" alt="Sunflower seed">
        </div>
      </section>
    </transition>

    <button class="ui-tutorial__skip" @click="skip">
      <p>{{ $translate('tutorial').close }}</p>
    </button>
  </div>
</template>

<script>
import { gsap } from 'gsap';
import { init, enter } from '@/scripts/animations';

export default {
  name: 'Tutorial',

  data() {
    return {
      phase: 0,
      timer: null,
    };
  },

  methods: {
    firstClick() {
      if (!this.isInTransition && this.phase === 0) this.incrementPhase();
    },
    incrementPhase() {
      if (this.phase === 2) {
        this.$store.commit('SET_MODE', 'playing');
      } else {
        this.phase += 1;
      }

      window.clearTimeout(this.timer);
      this.timer = null;
    },
    skip() {
      if (this.timer) {
        window.clearTimeout(this.timer);
        this.timer = null;
      }

      this.$store.commit('SET_MODE', 'playing');
    },
    initTimer() {
      this.timer = window.setTimeout(this.incrementPhase, 2200);
    },
    enter(el, done) {
      const text = el.querySelector('.ui-tutorial__content');
      const { phase } = el.dataset;
      const tl = gsap.timeline({
        onComplete: () => {
          if (this.phase !== 0) this.initTimer();
          done();
        },
      });

      init.get('fade')(text, { y: 20 });

      tl.addLabel('start')
        .add(enter.get('fade')(text, 0, 0.5), 'start');

      if (phase === 'tap') {
        const circle = el.querySelector('.ui-tutorial__circle');
        const circleText = el.querySelector('.ui-tutorial__circle p');

        init.get('fade')(circle, { scale: 0 });
        init.get('fade')(circleText, { y: 20 });

        tl.add(enter.get('fade')(circle, 0, 1), 'start+=0.2')
          .add(enter.get('fade')(circleText, 0, 0.5), 'start+=0.8');
      } else if (phase === 'leaf') {
        const leaf = el.querySelector('.ui-tutorial__leaf img');
        const leafText = el.querySelector('.ui-tutorial__leaf p');

        gsap.set(leaf, { scale: 0, transformOrigin: '50% 65%' });
        init.get('fade')(leafText, { y: 20 });

        tl.add(enter.get('fade')(leafText, 0, 0.5), 'start+=0.4')
          .to(leaf, { scale: 1, duration: 0.5, ease: 'elastic.out(0.5, 0.7)' }, 'start+=0.2');
      } else {
        const seeds = [...el.querySelectorAll('.ui-tutorial__seeds img')];
        const seedsText = el.querySelector('.ui-tutorial__seeds p');

        gsap.set(seeds, { scale: 0, transformOrigin: '50% 65%' });
        init.get('fade')(seedsText, { y: 20 });

        tl.add(enter.get('fade')(seedsText, 0, 0.5), 'start+=0.4')
          .to(seeds, {
            scale: 1,
            duration: 0.8,
            ease: 'elastic.out(0.5, 0.7)',
            stagger: 0.25,
          }, 'start+=0.2');
      }
    },
    leave(el, done) {
      gsap.to(el, {
        y: -50,
        autoAlpha: 0,
        duration: 0.5,
        ease: 'power3.easeOut',
        onComplete: done,
      });
    },
  },

  beforeDestroy() {
    if (this.timer) window.clearTimeout(this.timer);
  },
};
</script>

<style lang="scss">
@keyframes ripple {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(0.5);
    opacity: 0;
  }
}

.ui-tutorial {
  text-align: center;
  color: $color-white;
  pointer-events: all;

  &,
  &__phase,
  &__circle {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &,
  &__bg {
    @include full();
  }

  &__bg {
    background-color: rgba($color-brown, 0.7);
  }

  &__phase {
    margin-top: -15vh;
    height: 30vh;

    justify-content: space-between;
    flex-flow: column nowrap;
  }

  &__content,
  &__circle,
  &__leaf,
  &__seeds {
    position: relative;

    font-weight: 800;
    line-height: 1.6;

    font-size: 16px;

    @include mq(ipadP) {
      font-size: 18px;
    }

    @include mq(ds) {
      font-size: 20px;
    }

    @include mq(dxl) {
      font-size: 22px;
    }
  }

  &__content {
    padding: 0px 20vw;
  }

  &__circle {
    width: 20vh;
    height: 20vh;
    flex: 0 0 auto;
    margin-top: 10vh;

    color: inherit;
    cursor: pointer;

    @include mq($until: ipadL) {
      max-width: 200px;
      max-height: 200px;
    }

    &,
    .circle__repeater {
      border-radius: 50%;
      border: 1px solid $color-white;
    }

    .circle__repeater {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;

      @for $i from 6 through 1 {
        &:nth-child(#{$i}) {
          animation: ripple 4s infinite $ease-out;
          animation-delay: (500ms * $i);
        }
      }
    }
  }

  &__seeds,
  &__leaf {
    display: flex;
    align-items: center;
    justify-content: center;

    > * {
      flex: 0 0 auto;
    }

    img {
      width: 45px;
    }
  }

  &__leaf {
    p {
      position: relative;
      margin-left: 15px;
    }
  }

  &__seeds {
    p {
      margin: 0 35px;
    }
  }

  &__skip {
    position: absolute;
    bottom: 28px;
    left: 50%;

    color: inherit;
    @extend %font-small;

    transform: translateX(-50%);
    cursor: pointer;

    p {
      text-decoration: underline;
    }

    @include mq(ipadP) {
      bottom: 38px;
    }

    @include mq(dm) {
      bottom: 48px;
    }
  }
}
</style>
